import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/context/ThemeContext";
import { ToastProvider } from "@/context/ToastContext";
import { ConfirmProvider } from "@/context/ConfirmContext";
import { AuthProvider } from "@/context/AuthContext";
import { PWAProvider } from "@/context/PWAContext";

import { ProtectedRoute } from "@/components/layout/ProtectedRoute";
import { AppLayout } from "@/components/layout/AppLayout";

import { LoginPage } from "@/pages/auth/LoginPage";
import { ForgotPasswordPage } from "@/pages/auth/ForgotPasswordPage";
import { SetPasswordPage } from "@/pages/auth/SetPasswordPage";
import { ChangePasswordPage } from "@/pages/auth/ChangePasswordPage";
import { SetupTwoStepPage } from "@/pages/auth/SetupTwoStepPage";

import { DashboardPage } from "@/pages/dashboard/DashboardPage";
import { MyDayPage } from "@/pages/dashboard/myday/MyDayPage";
import { AskRedirect } from "@/pages/ask/AskRedirect";
import TasksPage from "@/pages/tasks/TasksPage";
import CalendarPage from "@/pages/calendar/CalendarPage";
import { LeadsPage } from "@/pages/leads/LeadsPage";
import { LeadFormPage } from "@/pages/leads/LeadFormPage";
import { LeadImportPage } from "@/pages/leads/LeadImportPage";
import { LeadDetailPage } from "@/pages/leads/LeadDetailPage";
import { DealsPage } from "@/pages/deals/DealsPage";
import { DealDetailPage } from "@/pages/deals/DealDetailPage";
import { AccountsPage } from "@/pages/accounts/AccountsPage";
import { AccountDetailPage } from "@/pages/accounts/AccountDetailPage";
import { ContactsPage } from "@/pages/contacts/ContactsPage";
import { ContactDetailPage } from "@/pages/contacts/ContactDetailPage";
import { ProductsPage } from "@/pages/products/ProductsPage";
import { QuotesPage } from "@/pages/quotes/QuotesPage";
import { QuoteEditorPage } from "@/pages/quotes/QuoteEditorPage";
import { OrdersPage } from "@/pages/orders/OrdersPage";
import { SamplesPage } from "@/pages/samples/SamplesPage";
import { VisitsPage } from "@/pages/visits/VisitsPage";
import { SampleDetailPage } from "@/pages/samples/SampleDetailPage";
import { PriceListsPage } from "@/pages/pricelists/PriceListsPage";
import { PriceListDetailPage } from "@/pages/pricelists/PriceListDetailPage";
import { OrderDetailPage } from "@/pages/orders/OrderDetailPage";
import { ProposalsPage } from "@/pages/proposals/ProposalsPage";
import { ProposalEditorPage } from "@/pages/proposals/ProposalEditorPage";
import { AgreementsPage } from "@/pages/agreements/AgreementsPage";
import { AgreementEditorPage } from "@/pages/agreements/AgreementEditorPage";
import { ProposalTemplatesPage } from "@/pages/settings/ProposalTemplatesPage";
import { QuoteDesignPage } from "@/pages/settings/QuoteDesignPage";
import { AgreementTemplatesPage } from "@/pages/settings/AgreementTemplatesPage";
import { PublicProposalPage } from "@/pages/public/PublicProposalPage";
import { PublicAgreementPage } from "@/pages/public/PublicAgreementPage";
import { UsersPage } from "@/pages/users/UsersPage";
import { NotificationsPage } from "@/pages/notifications/NotificationsPage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { RolesPage } from "@/pages/roles/RolesPage";
import { CompanyPage } from "@/pages/settings/CompanyPage";
import { CurrenciesCountriesPage } from "@/pages/settings/CurrenciesCountriesPage";
import { PaymentTermsPage } from "@/pages/settings/PaymentTermsPage";
import { PipelinePage } from "@/pages/settings/PipelinePage";
import { SettingRulePage } from "@/pages/settings/SettingRulePage";
import { ApprovalsSettingsPage } from "@/pages/settings/ApprovalsSettingsPage";
import { SecurityLogPage } from "@/pages/settings/SecurityLogPage";
import { SignInSecurityPage } from "@/pages/settings/SignInSecurityPage";
import { StoragePage } from "@/pages/settings/StoragePage";
import { AiUsagePage } from "@/pages/settings/AiUsagePage";
import { CallingSettingsPage } from "@/pages/settings/CallingSettingsPage";
import { PushSettingsPage } from "@/pages/settings/PushSettingsPage";
import { PreferencesPage } from "@/pages/settings/PreferencesPage";
import { NotificationRulesPage } from "@/pages/settings/NotificationRulesPage";
import { CallsPage } from "@/pages/calls/CallsPage";
import { MasterLookupPage } from "@/pages/settings/MasterLookupPage";
import { ReportsPage } from "@/pages/reports/ReportsPage";
import { UserReportPage } from "@/pages/reports/UserReportPage";
import { TargetsPage } from "@/pages/targets/TargetsPage";
import { EmailsPage } from "@/pages/emails/EmailsPage";
import { WhatsAppPage } from "@/pages/whatsapp/WhatsAppPage";
import { CampaignDetailPage } from "@/pages/whatsapp/CampaignDetailPage";
import { WhatsAppSettingsPage } from "@/pages/settings/WhatsAppSettingsPage";
import { EmailSettingsPage } from "@/pages/settings/EmailSettingsPage";
import { LeadSourcesPage } from "@/pages/settings/LeadSourcesPage";

import "./index.css";

export function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <ToastProvider>
        <ConfirmProvider>
          <AuthProvider>
            <PWAProvider>
              <Routes>
              {/* Public Authentication Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/set-password" element={<SetPasswordPage />} />
              {/* Customer link to a sent proposal (no login) */}
              <Route path="/p/:token" element={<PublicProposalPage />} />
              {/* Signer link to a sent agreement (no login) */}
              <Route path="/a/:token" element={<PublicAgreementPage />} />
              {/* Old reset-code page: codes are retired, so send people to request a link */}
              <Route path="/reset-password/*" element={<Navigate to="/forgot-password" replace />} />
              <Route
                path="/change-password"
                element={
                  <ProtectedRoute>
                    <ChangePasswordPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/setup-two-step"
                element={
                  <ProtectedRoute>
                    <SetupTwoStepPage />
                  </ProtectedRoute>
                }
              />

              {/* Protected Management Routes */}
              <Route
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/dashboard/my-day" element={<MyDayPage />} />
                <Route path="/ask" element={<AskRedirect />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route
                  path="/emails"
                  element={
                    <ProtectedRoute permission="email.read">
                      <EmailsPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/mailbox" element={<Navigate to="/emails?tab=settings" replace />} />
                <Route
                  path="/whatsapp/campaigns/:id"
                  element={
                    <ProtectedRoute permission="whatsapp.campaign">
                      <CampaignDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/whatsapp"
                  element={
                    <ProtectedRoute permission="whatsapp.read">
                      <WhatsAppPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/calendar"
                  element={
                    <ProtectedRoute permission="calendar.read">
                      <CalendarPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/tasks"
                  element={
                    <ProtectedRoute permission="activity.read">
                      <TasksPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/leads"
                  element={
                    <ProtectedRoute permission="lead.read">
                      <LeadsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/leads/import"
                  element={
                    <ProtectedRoute permission="lead.import">
                      <LeadImportPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/leads/new"
                  element={
                    <ProtectedRoute permission="lead.create">
                      <LeadFormPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/leads/:id/edit"
                  element={
                    <ProtectedRoute permission="lead.update">
                      <LeadFormPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/leads/:id"
                  element={
                    <ProtectedRoute permission="lead.read">
                      <LeadDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/deals"
                  element={
                    <ProtectedRoute permission="deal.read">
                      <DealsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/deals/:id"
                  element={
                    <ProtectedRoute permission="deal.read">
                      <DealDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/products"
                  element={
                    <ProtectedRoute permission="product.read">
                      <ProductsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/proposals"
                  element={
                    <ProtectedRoute permission="proposal.read">
                      <ProposalsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/proposals/:id"
                  element={
                    <ProtectedRoute permission="proposal.read">
                      <ProposalEditorPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agreements"
                  element={
                    <ProtectedRoute permission="agreement.read">
                      <AgreementsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agreements/:id"
                  element={
                    <ProtectedRoute permission="agreement.read">
                      <AgreementEditorPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/quotes"
                  element={
                    <ProtectedRoute permission="quote.read">
                      <QuotesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/quotes/:id"
                  element={
                    <ProtectedRoute permission="quote.read">
                      <QuoteEditorPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/samples"
                  element={
                    <ProtectedRoute permission="sample.read">
                      <SamplesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/samples/:id"
                  element={
                    <ProtectedRoute permission="sample.read">
                      <SampleDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/price-lists"
                  element={
                    <ProtectedRoute permission="price_list.manage">
                      <PriceListsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/price-lists/:id"
                  element={
                    <ProtectedRoute permission="price_list.manage">
                      <PriceListDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/orders"
                  element={
                    <ProtectedRoute permission="order.read">
                      <OrdersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/orders/:id"
                  element={
                    <ProtectedRoute permission="order.read">
                      <OrderDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <ProtectedRoute permission="report.view_own">
                      <ReportsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports/user/:id"
                  element={
                    <ProtectedRoute permission="report.view_own">
                      <UserReportPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/targets"
                  element={
                    <ProtectedRoute permission="target.read">
                      <TargetsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/accounts"
                  element={
                    <ProtectedRoute permission="account.read">
                      <AccountsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/accounts/:id"
                  element={
                    <ProtectedRoute permission="account.read">
                      <AccountDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/contacts"
                  element={
                    <ProtectedRoute permission="contact.read">
                      <ContactsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/contacts/:id"
                  element={
                    <ProtectedRoute permission="contact.read">
                      <ContactDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <ProtectedRoute permission="user.manage">
                      <UsersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/roles"
                  element={
                    <ProtectedRoute permission="role.manage">
                      <RolesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/company"
                  element={
                    <ProtectedRoute permission="setting.manage">
                      <CompanyPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/currencies"
                  element={
                    <ProtectedRoute permission="setting.manage">
                      <CurrenciesCountriesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/payment-terms"
                  element={
                    <ProtectedRoute permission="setting.manage">
                      <PaymentTermsPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/settings/countries" element={<Navigate to="/settings/currencies" replace />} />
                <Route
                  path="/settings/pipelines"
                  element={
                    <ProtectedRoute permission="setting.manage">
                      <PipelinePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/approvals"
                  element={
                    <ProtectedRoute permission="setting.manage">
                      <ApprovalsSettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/rules"
                  element={
                    <ProtectedRoute permission="setting.manage">
                      <SettingRulePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/ai"
                  element={
                    <ProtectedRoute roles={["SUPER_ADMIN"]}>
                      <AiUsagePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/storage"
                  element={
                    <ProtectedRoute roles={["SUPER_ADMIN"]}>
                      <StoragePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/sign-in-security"
                  element={
                    <ProtectedRoute roles={["SUPER_ADMIN"]}>
                      <SignInSecurityPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/security-log"
                  element={
                    <ProtectedRoute roles={["SUPER_ADMIN"]}>
                      <SecurityLogPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/whatsapp"
                  element={
                    <ProtectedRoute permission="whatsapp.admin">
                      <WhatsAppSettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/visits"
                  element={
                    <ProtectedRoute permission="visit.read">
                      <VisitsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/calls"
                  element={
                    <ProtectedRoute permission="call.history">
                      <CallsPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/settings/my-calling" element={<Navigate to="/settings/preferences?tab=calling" replace />} />
                <Route path="/settings/incoming-calls" element={<Navigate to="/settings/calling?tab=incoming" replace />} />
                <Route
                  path="/settings/preferences"
                  element={
                    <ProtectedRoute>
                      <PreferencesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/notification-rules"
                  element={
                    <ProtectedRoute permission="setting.manage">
                      <NotificationRulesPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/settings/display" element={<Navigate to="/settings/preferences?tab=display" replace />} />
                <Route path="/settings/notifications" element={<Navigate to="/settings/preferences?tab=notifications" replace />} />
                <Route
                  path="/settings/push"
                  element={
                    <ProtectedRoute permission="setting.manage">
                      <PushSettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/calling"
                  element={
                    <ProtectedRoute permission="call.admin">
                      <CallingSettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/quote-design"
                  element={
                    <ProtectedRoute permission="setting.manage">
                      <QuoteDesignPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/proposal-templates"
                  element={
                    <ProtectedRoute permission="proposal_template.manage">
                      <ProposalTemplatesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/agreement-templates"
                  element={
                    <ProtectedRoute permission="agreement_template.manage">
                      <AgreementTemplatesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/lead-sources"
                  element={
                    <ProtectedRoute permission="lead.source_manage">
                      <LeadSourcesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/email"
                  element={<Navigate to="/emails?tab=settings" replace />}
                />
                <Route
                  path="/settings/mailbox"
                  element={<Navigate to="/emails?tab=settings" replace />}
                />
                <Route
                  path="/settings/masters"
                  element={
                    <ProtectedRoute permission="setting.manage">
                      <MasterLookupPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Routes>
          </PWAProvider>
        </AuthProvider>
        </ConfirmProvider>
        </ToastProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
